/**
 * Every text, email, note and event in a record's Activity log. One row per
 * message, linked to every record it is about, so the same day-before text
 * shows on the job, its location and its client
 * (docs/majik/DATA-ARCHITECTURE-SPEC.md → Communications).
 *
 * Until Go live, nothing leaves the app: a message a person or an automation
 * sends is saved with the status `would_send` and its full wording, and no
 * vendor is called (docs/DECISIONS.md D-006). Pure.
 */
import { z } from 'zod'
import { recordIdSchema } from '../shared/record-id.ts'

export const channels = ['sms', 'email', 'internal_note', 'system'] as const
export type Channel = (typeof channels)[number]

export const directions = ['outbound', 'inbound', 'internal'] as const

/** `would_send`: written while Go live is off — it would have been sent, and wasn't. */
export const communicationStatuses = [
  'would_send',
  'sent',
  'failed',
  'received',
  'recorded',
] as const
export type CommunicationStatus = (typeof communicationStatuses)[number]

export const purposes = [
  'job_notification',
  'cleaner_schedule',
  'staff_broadcast',
  'inspection',
  'invoice',
  'payment',
  /** The "it has been a while" email to a client (Monday's Client Hub updates). */
  'customer_reminder',
  /** An automation that failed and said so (Monday's "AUTOMATION ERROR" updates). */
  'automation_error',
  'general',
] as const
export type Purpose = (typeof purposes)[number]

/** Who it went to or came from — the coloured pill on every Activity entry. */
export const partyKinds = [
  'customer',
  'cleaner',
  'billing',
  'inspector',
  'quickbooks',
  'internal',
] as const
export type PartyKind = (typeof partyKinds)[number]

export const linkKinds = [
  'job',
  'client',
  'location',
  'invoice',
  'cleaner',
  'contact',
  'order',
  'inspection',
] as const
export type LinkKind = (typeof linkKinds)[number]

export const communicationSchema = z
  .object({
    id: recordIdSchema,
    channel: z.enum(channels),
    direction: z.enum(directions),
    purpose: z.enum(purposes),
    status: z.enum(communicationStatuses),
    /** For an event or a note: what happened ("Marked completed"). Texts and emails derive theirs. */
    title: z.string().nullable().default(null),
    subject: z.string().nullable().default(null),
    body: z.string().default(''),
    party: z.object({
      kind: z.enum(partyKinds),
      contactId: recordIdSchema.nullable().default(null),
      /** As it was when the message went — so it still reads if they are renamed. */
      name: z.string().nullable().default(null),
    }),
    /** What drove it: "Automatic · day-before notice rule…", "Sent by Yasmin from the app". */
    note: z.string().default(''),
    /** At least one, except a staff broadcast, which may name no one (Monday's never did). */
    links: z.array(z.object({ kind: z.enum(linkKinds), id: recordIdSchema })),
    /** When it was sent, received or happened. */
    at: z.iso.datetime(),
    /** An inbound message nobody has read yet has none. */
    readAt: z.iso.datetime().nullable().default(null),
    /**
     * A note's writer, as they were named when they wrote it. With Admins, they
     * may change or delete it (notes.ts). Messages and events have none.
     */
    author: z.object({ userId: z.string().nullable(), name: z.string() }).optional(),
    /** A note changed after it was written: when, and by whom (their name then). */
    editedAt: z.iso.datetime().optional(),
    editedBy: z.string().optional(),
    /** A deleted note: gone from every Activity log, back with Undo (docs/SECURITY.md). */
    deletedAt: z.iso.datetime().optional(),
  })
  .refine((m) => m.links.length > 0 || m.purpose === 'staff_broadcast', {
    message: 'A message is about at least one record — only a staff broadcast may name none.',
    path: ['links'],
  })
export type Communication = z.infer<typeof communicationSchema>

export function isAbout(message: Pick<Communication, 'links'>, kind: LinkKind, id: string) {
  return message.links.some((link) => link.kind === kind && link.id === id)
}

/** "Text sent", "Email received", "Text — would send", or the event's own title. */
export function communicationTitle(
  message: Pick<Communication, 'channel' | 'direction' | 'status' | 'title'>,
): string {
  if (message.title) return message.title
  const medium = message.channel === 'email' ? 'Email' : message.channel === 'sms' ? 'Text' : 'Note'
  if (message.status === 'would_send') return `${medium} — would send`
  if (message.status === 'failed') return `${medium} failed`
  return message.direction === 'inbound' ? `${medium} received` : `${medium} sent`
}

const PARTY: Record<PartyKind, string> = {
  customer: 'customer',
  cleaner: 'cleaner',
  billing: 'billing contact',
  inspector: 'inspector',
  quickbooks: 'QuickBooks',
  internal: 'Internal',
}

/** The pill: "To customer · Georgia", "From QuickBooks", "Internal". */
export function partyText(message: Pick<Communication, 'direction' | 'party'>): string {
  const { kind, name } = message.party
  if (kind === 'internal') return PARTY.internal
  if (kind === 'quickbooks')
    return message.direction === 'outbound' ? 'To QuickBooks' : 'From QuickBooks'
  const towards = message.direction === 'inbound' ? 'From' : 'To'
  return name ? `${towards} ${PARTY[kind]} · ${name}` : `${towards} ${PARTY[kind]}`
}
